import { put, select, takeLatest } from 'redux-saga/effects';
import {
  getVenue,
  getVenueBySlug,
  searchVenues,
} from '../../graphql/customQueries';
import filterResults from '../../utils/filterResults';
import { callGraphqlWithToken, dispatchError } from '../helpers';
import * as ACTIONS from './VenuesAction';
import * as TYPES from './VenuesTypes';
import { getConfig, getUser } from '../selectors';
import { VENUE_TYPES } from '../../utils/constants/FilterTypes';
import { VENUE_SORT_DIRECTIONS } from '../../utils/constants/VenueSortDirection';
import {
  filterUnavailableRelatedEvents,
  isPastEvent,
  isVenueAvailable,
} from '../../utils/eventHelpers';
import { clearPermalinkRouteRedirect } from '../../utils/sessionStorageHelper';
import { ERROR_PAGE_TYPES } from '../../utils/constants/ErrorPageTypes';
import { getVenuesByCuratedPick } from '../../pages/search-refresh/helpers/curatedPickHelpers';

export function* getVenueRequestHandler(data) {
  try {
    const result = yield callGraphqlWithToken({
      query: getVenue,
      variables: { id: data.payload },
    });
    if (result.data.getVenue === null) {
      throw new Error(ERROR_PAGE_TYPES.NO_VENUE.value);
    }
    const venue = filterUnavailableRelatedEvents(result.data.getVenue);
    yield put(ACTIONS.actionGetVenueSuccess(venue));
  } catch (e) {
    console.error(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionGetVenueFail(e));
  }
}

export function* searchEventsRequestHandler(data) {
  try {
    const config = yield select(getConfig);
    const isTestUser = data.payload?.isTestUser;
    const filters = {
      isHidden: {
        eq: false,
      },
      isActive: {
        eq: true,
      },
      venueType: {
        eq: VENUE_TYPES.EVENT,
      },
      ...(!isTestUser && {
        isTest: {
          eq: false,
        },
      }),
    };
    const sort = {
      field: 'sortOrder',
      direction:
        VENUE_SORT_DIRECTIONS[config.venueSortDirection] ||
        VENUE_SORT_DIRECTIONS.asc,
    };
    const result = yield callGraphqlWithToken({
      query: searchVenues,
      variables: {
        filter: filters,
        sort,
      },
    });
    yield put(
      ACTIONS.actionGetEventsSuccess(
        result.data.searchVenues?.items?.filter((venue) => !isPastEvent(venue))
      )
    );
  } catch (e) {
    console.error(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionGetEventsFail(e));
  }
}

export function* findVenuesRequestHandler(data) {
  try {
    if (data.payload.keyword) {
      yield put(ACTIONS.actionSearchVenuesRequest(data.payload));
    } else {
      yield put(ACTIONS.actionVenuesByMarketRequest(data.payload));
    }
  } catch (e) {
    console.error(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionFindVenuesFail(e));
  }
}

function* callSearchVenues(payload) {
  // Create payload for searchVenues
  const { keyword, marketId, filters } = payload;
  const user = yield select(getUser);
  const isTestUser = user.user.isTest;
  let nextToken;
  const venues = [];

  const massaged_filters = {
    isActive: { eq: true },
    isHidden: { eq: false },
    ...(filters.venueType?.length === 1 && {
      venueType: { eq: filters.venueType[0].venueType },
    }),
    ...(!isTestUser && { isTest: { ne: true } }),
    ...(filters.isInsider !== undefined && {
      isInsider: { eq: filters.isInsider },
    }),
    ...(filters.isSignatureCollection !== undefined && {
      isSignatureCollection: { eq: filters.isSignatureCollection },
    }),
    ...(marketId ? { marketId: { eq: marketId } } : {}),
    ...(keyword
      ? {
          or: [
            { cuisineNames: { matchPhrasePrefix: keyword } },
            { name: { matchPhrasePrefix: keyword } },
            { normalizedName: { matchPhrasePrefix: keyword } },
          ],
        }
      : {}),
  };

  do {
    const result = yield callGraphqlWithToken({
      query: searchVenues,
      variables: {
        filter: massaged_filters,
        nextToken,
        date: payload.date,
        startTime: payload.startTime,
        endTime: payload.endTime,
        partySize: payload.partySize,
      },
    });

    result.data.searchVenues.items = result.data.searchVenues?.items?.filter(
      (venue) => isVenueAvailable(venue)
    );

    venues.push(...result.data.searchVenues.items);
    nextToken = result.data.searchVenues.nextToken;
  } while (nextToken != null);

  return venues;
}

export function* searchVenuesRequestHandler(data) {
  try {
    const payload = data.payload;
    let { marketId, filters } = payload;
    let venues = yield callSearchVenues(payload);
    let venuesByCuratedPick = null;
    let filteredVenues = filterResults(filters, venues);

    if (marketId != null) {
      if (filteredVenues.length === 0) {
        marketId = null;
        venues = yield callSearchVenues({ ...payload, marketId });
      } else {
        venuesByCuratedPick = yield getVenuesByCuratedPick(filteredVenues);
      }
    }

    yield put(
      ACTIONS.actionSetVenues({
        list: filterResults(filters, venues),
        venuesByCuratedPick: venuesByCuratedPick,
      })
    );
    yield put(
      ACTIONS.actionSearchVenuesSuccess({
        inLocalMarket: marketId != null,
      })
    );
  } catch (e) {
    console.error(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionSearchVenuesFail(e));
  }
}

export function* venuesByMarketRequestHandler(data) {
  try {
    const venues = [];
    const user = yield select(getUser);
    const config = yield select(getConfig);
    const isTestUser = user.user.isTest;
    let nextToken;

    do {
      const result = yield callGraphqlWithToken({
        query: searchVenues,
        variables: {
          filter: {
            isActive: { eq: true },
            marketId: { eq: data.payload.marketId },
            isHidden: { eq: false },
            ...(data.payload.filters.venueType?.length === 1 && {
              venueType: {
                eq: data.payload.filters.venueType[0].venueType,
              },
            }),
            ...(!isTestUser && { isTest: { ne: true } }),
          },
          sort: {
            direction:
              VENUE_SORT_DIRECTIONS[config.venueSortDirection] ||
              VENUE_SORT_DIRECTIONS.asc,
            field: 'sortOrder',
          },
          nextToken,
          date: data.payload.date,
          startTime: data.payload.startTime,
          endTime: data.payload.endTime,
          partySize: data.payload.partySize,
        },
      });

      result.data.searchVenues.items = result.data.searchVenues?.items?.filter(
        (venue) => isVenueAvailable(venue)
      );

      venues.push(...result.data.searchVenues.items);
      nextToken = result.data.searchVenues.nextToken;
    } while (nextToken != null);

    const filteredVenues = filterResults(data.payload.filters, venues);
    const venuesByCuratedPick = yield getVenuesByCuratedPick(filteredVenues);
    yield put(
      ACTIONS.actionSetVenues({
        list: filteredVenues,
        venuesByCuratedPick: venuesByCuratedPick,
      })
    );
    yield put(ACTIONS.actionVenuesByMarketSuccess());
  } catch (e) {
    console.error(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionVenuesByMarketFail(e));
  }
}

export function* getVenueBySlugRequestHandler(data) {
  try {
    clearPermalinkRouteRedirect();
    const result = yield callGraphqlWithToken({
      query: getVenueBySlug,
      variables: {
        venueSlug: data.payload,
        filter: {
          isActive: { eq: true },
        }
      }, //Update with index in dynamoDB
    });
    let venue = result?.data?.venuesByVenueSlug?.items[0];
    let venueId = venue?.id;
    const { permaLinks } = yield select(getConfig);
    let pathname = '/';
    if (venueId) {
      pathname = `/venue/${venueId}`;
    } else if (permaLinks[data.payload]) {
      pathname = permaLinks[data.payload];
    }
    yield put(ACTIONS.actionGetVenueBySlugSuccess(pathname));
    yield put(ACTIONS.actionGetVenueSuccess(venue));
  } catch (e) {
    console.error(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionGetVenueFail(e));
  }
}

export default function* venuesSaga() {
  yield takeLatest(TYPES.FIND_VENUES_REQUEST, findVenuesRequestHandler);
  yield takeLatest(TYPES.VENUES_BY_MARKET_REQUEST, venuesByMarketRequestHandler);
  yield takeLatest(TYPES.SEARCH_VENUES_REQUEST, searchVenuesRequestHandler);
  yield takeLatest(TYPES.GET_VENUE_REQUEST, getVenueRequestHandler);
  yield takeLatest(TYPES.GET_EVENTS_REQUEST, searchEventsRequestHandler);
  yield takeLatest(TYPES.GET_VENUE_BY_SLUG_REQUEST, getVenueBySlugRequestHandler);
}
